import React, { useState, useRef, useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

const INDENT = 28;
const LONG_PRESS_MS = 250;

/**
 * MobileDragTree — touch-friendly tree with long-press drag-and-drop reordering.
 *
 * Props:
 *   value          - Array of tree nodes ({ key, label, children, scope, ... })
 *   expandedKeys   - Object { [nodeKey]: true }
 *   onToggle       - (newExpandedKeys) => void
 *   nodeTemplate   - (node) => JSX — renders content for each row
 *   onNodeDoubleClick - ({ node }) => void
 *   onDragDrop     - ({ value, dragNode, dropNode }) => void
 *   filter         - boolean (show search)
 *   filterPlaceholder - string
 */
export default function MobileDragTree({
    value,
    expandedKeys,
    onToggle,
    nodeTemplate,
    onNodeDoubleClick,
    onDragDrop,
    filter = true,
    filterPlaceholder = "Search...",
}) {
    const [filterText, setFilterText] = useState("");
    const [draggedKey, setDraggedKey] = useState(null);
    const [dropTargetKey, setDropTargetKey] = useState(null);
    const [dropPosition, setDropPosition] = useState(null); // "before" | "inside" | "after"
    const longPressTimer = useRef(null);
    const dragNodeRef = useRef(null);
    const touchStartY = useRef(0);
    const scrollContainerRef = useRef(null);
    const nodeRectsRef = useRef([]);
    const isDragging = useRef(false);
    const dragGhostRef = useRef(null);

    // Flatten tree for rendering
    const flattenTree = useCallback(
        (nodes, depth = 0, parentKey = null) => {
            const result = [];
            if (!nodes) return result;
            for (const node of nodes) {
                const hasChildren = node.children && node.children.length > 0;
                const isExpanded = expandedKeys[node.key] === true;
                result.push({ node, depth, hasChildren, isExpanded, parentKey });
                if (hasChildren && isExpanded) {
                    result.push(...flattenTree(node.children, depth + 1, node.key));
                }
            }
            return result;
        },
        [expandedKeys]
    );

    // Filter nodes recursively
    const filterNodes = useCallback((nodes, text) => {
        if (!text) return nodes;
        const lower = text.toLowerCase();
        return nodes.reduce((acc, node) => {
            const match = node.label?.toLowerCase().includes(lower);
            const filteredChildren = node.children
                ? filterNodes(node.children, text)
                : [];
            if (match || filteredChildren.length > 0) {
                acc.push({
                    ...node,
                    children: filteredChildren.length > 0 ? filteredChildren : node.children,
                });
            }
            return acc;
        }, []);
    }, []);

    const displayNodes = useMemo(
        () => filterNodes(value, filterText),
        [value, filterText, filterNodes]
    );

    const flatList = useMemo(
        () => flattenTree(displayNodes),
        [displayNodes, flattenTree]
    );

    // Toggle expand/collapse
    const toggleNode = useCallback(
        (key) => {
            const next = { ...expandedKeys };
            if (next[key]) {
                delete next[key];
            } else {
                next[key] = true;
            }
            onToggle(next);
        },
        [expandedKeys, onToggle]
    );

    // — Reorder helpers —
    const removeNode = (nodes, key) => {
        const result = [];
        for (const n of nodes) {
            if (n.key === key) continue;
            const copy = { ...n };
            if (copy.children) copy.children = removeNode(copy.children, key);
            result.push(copy);
        }
        return result;
    };

    const findNode = (nodes, key) => {
        for (const n of nodes) {
            if (n.key === key) return n;
            if (n.children) {
                const found = findNode(n.children, key);
                if (found) return found;
            }
        }
        return null;
    };

    const insertNode = (nodes, targetKey, dragNode, position) => {
        const result = [];
        for (const n of nodes) {
            if (n.key === targetKey) {
                if (position === "before") {
                    result.push(dragNode);
                    result.push({ ...n, children: n.children ? insertNode(n.children, targetKey, dragNode, null) : n.children });
                } else if (position === "after") {
                    result.push({ ...n, children: n.children ? insertNode(n.children, targetKey, dragNode, null) : n.children });
                    result.push(dragNode);
                } else if (position === "inside") {
                    result.push({
                        ...n,
                        children: [...(n.children || []), dragNode],
                    });
                } else {
                    result.push(n);
                }
            } else {
                const copy = { ...n };
                if (copy.children) copy.children = insertNode(copy.children, targetKey, dragNode, position);
                result.push(copy);
            }
        }
        return result;
    };

    const performDrop = useCallback(() => {
        if (!draggedKey || !dropTargetKey || !dropPosition) return;
        const dragNode = findNode(value, draggedKey);
        const dropNode = findNode(value, dropTargetKey);
        if (!dragNode || draggedKey === dropTargetKey) return;

        let treeWithout = removeNode(value, draggedKey);
        let newTree = insertNode(treeWithout, dropTargetKey, dragNode, dropPosition);

        onDragDrop({ value: newTree, dragNode, dropNode });
    }, [draggedKey, dropTargetKey, dropPosition, value, onDragDrop]);

    // — Touch handlers —
    const collectNodeRects = () => {
        const container = scrollContainerRef.current;
        if (!container) return;
        const rows = container.querySelectorAll("[data-node-key]");
        nodeRectsRef.current = Array.from(rows).map((el) => {
            const rect = el.getBoundingClientRect();
            return { key: el.getAttribute("data-node-key"), top: rect.top, bottom: rect.bottom, height: rect.height };
        });
    };

    const createGhost = (el) => {
        const rect = el.getBoundingClientRect();
        const ghost = el.cloneNode(true);
        ghost.style.position = "fixed";
        ghost.style.left = `${rect.left}px`;
        ghost.style.top = `${rect.top}px`;
        ghost.style.width = `${rect.width}px`;
        ghost.style.height = `${rect.height}px`;
        ghost.style.zIndex = "9999";
        ghost.style.opacity = "0.85";
        ghost.style.borderRadius = "10px";
        ghost.style.boxShadow = "0 8px 32px rgba(0,0,0,0.18)";
        ghost.style.pointerEvents = "none";
        ghost.style.transition = "transform 0.05s ease-out";
        ghost.style.background = "#fff";
        document.body.appendChild(ghost);
        dragGhostRef.current = { el: ghost, startY: rect.top };
    };

    const removeGhost = () => {
        if (dragGhostRef.current?.el) {
            dragGhostRef.current.el.remove();
            dragGhostRef.current = null;
        }
    };

    const handleTouchStart = (e, nodeKey) => {
        const touch = e.touches[0];
        touchStartY.current = touch.clientY;
        const rowEl = e.currentTarget.closest("[data-node-key]");

        longPressTimer.current = setTimeout(() => {
            isDragging.current = true;
            setDraggedKey(nodeKey);
            dragNodeRef.current = nodeKey;
            collectNodeRects();
            if (rowEl) createGhost(rowEl);

            // Haptic feedback
            if (navigator.vibrate) navigator.vibrate(30);
        }, LONG_PRESS_MS);
    };

    const handleTouchMove = (e) => {
        if (!isDragging.current) {
            // Cancel long press if finger moved too much
            const touch = e.touches[0];
            if (Math.abs(touch.clientY - touchStartY.current) > 10) {
                clearTimeout(longPressTimer.current);
            }
            return;
        }
        e.preventDefault();

        const touch = e.touches[0];
        const y = touch.clientY;

        // Move ghost
        if (dragGhostRef.current) {
            const delta = y - touchStartY.current;
            dragGhostRef.current.el.style.top = `${dragGhostRef.current.startY + delta}px`;
        }

        // Find drop target
        let foundKey = null;
        let pos = null;
        for (const rect of nodeRectsRef.current) {
            if (rect.key === dragNodeRef.current) continue;
            if (y >= rect.top && y <= rect.bottom) {
                const third = rect.height / 3;
                if (y < rect.top + third) {
                    pos = "before";
                } else if (y > rect.bottom - third) {
                    pos = "after";
                } else {
                    pos = "inside";
                }
                foundKey = rect.key;
                break;
            }
        }
        setDropTargetKey(foundKey);
        setDropPosition(pos);

        // Auto-scroll
        const container = scrollContainerRef.current;
        if (container) {
            const containerRect = container.getBoundingClientRect();
            const threshold = 50;
            if (y < containerRect.top + threshold) {
                container.scrollTop -= 8;
            } else if (y > containerRect.bottom - threshold) {
                container.scrollTop += 8;
            }
        }
    };

    const handleTouchEnd = () => {
        clearTimeout(longPressTimer.current);
        if (isDragging.current) {
            performDrop();
            isDragging.current = false;
            setDraggedKey(null);
            setDropTargetKey(null);
            setDropPosition(null);
            dragNodeRef.current = null;
            removeGhost();
        }
    };

    // Scope-based styling
    const getScopeStyle = (node, depth) => {
        const scope = node.key?.split("-")[0];
        if (scope === "cat" && depth === 0) {
            return {
                backgroundColor: "rgba(255,144,21,0.06)",
                borderLeft: "3px solid #FF9015",
            };
        }
        if (scope === "cat" && depth > 0) {
            return {
                backgroundColor: "rgba(255,144,21,0.03)",
                borderLeft: "3px solid rgba(255,144,21,0.4)",
            };
        }
        return {
            backgroundColor: "transparent",
            borderLeft: "3px solid transparent",
        };
    };

    const getDropIndicatorStyle = (nodeKey) => {
        if (nodeKey !== dropTargetKey || !dropPosition) return {};
        if (dropPosition === "before") {
            return { borderTop: "2px solid #FF9015" };
        }
        if (dropPosition === "after") {
            return { borderBottom: "2px solid #FF9015" };
        }
        if (dropPosition === "inside") {
            return { boxShadow: "inset 0 0 0 2px rgba(255,144,21,0.4)" };
        }
        return {};
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            {/* Search */}
            {filter && (
                <Box sx={{ px: 1.5, py: 1, flexShrink: 0 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder={filterPlaceholder}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 20, color: "text.disabled" }} />
                                </InputAdornment>
                            ),
                            endAdornment: filterText ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setFilterText("")}>
                                        <CloseIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "10px",
                                fontSize: "16px",
                                backgroundColor: "rgba(0,0,0,0.03)",
                                "& fieldset": { borderColor: "rgba(0,0,0,0.08)" },
                            },
                        }}
                    />
                </Box>
            )}

            {/* Tree rows */}
            <Box
                ref={scrollContainerRef}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchEnd}
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    WebkitOverflowScrolling: isDragging.current ? "auto" : "touch",
                    pb: 10,
                }}
            >
                {flatList.length === 0 && (
                    <Box sx={{ textAlign: "center", py: 6, color: "text.disabled" }}>
                        <Typography variant="body2">No items found</Typography>
                    </Box>
                )}

                {flatList.map(({ node, depth, hasChildren, isExpanded }) => (
                    <Box
                        key={node.key}
                        data-node-key={node.key}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            pl: `${depth * INDENT + 8}px`,
                            pr: 1,
                            py: 0.75,
                            minHeight: 48,
                            transition: "background-color 0.15s, border-color 0.15s",
                            borderBottom: "1px solid rgba(0,0,0,0.04)",
                            opacity: draggedKey === node.key ? 0.3 : 1,
                            ...getScopeStyle(node, depth),
                            ...getDropIndicatorStyle(node.key),
                        }}
                    >
                        {/* Drag handle */}
                        <Box
                            onTouchStart={(e) => handleTouchStart(e, node.key)}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 32,
                                height: 40,
                                flexShrink: 0,
                                cursor: "grab",
                                touchAction: "none",
                                color: "text.disabled",
                                mr: 0.25,
                            }}
                        >
                            <DragIndicatorIcon sx={{ fontSize: 20 }} />
                        </Box>

                        {/* Expand/collapse toggle */}
                        {hasChildren ? (
                            <IconButton
                                size="small"
                                onClick={() => toggleNode(node.key)}
                                sx={{
                                    width: 30,
                                    height: 30,
                                    flexShrink: 0,
                                    transition: "transform 0.2s",
                                    transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                                }}
                            >
                                <ExpandMoreIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        ) : (
                            <Box sx={{ width: 30, flexShrink: 0 }} />
                        )}

                        {/* Node content (via nodeTemplate) */}
                        <Box
                            sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}
                            onDoubleClick={() => onNodeDoubleClick?.({ node })}
                        >
                            {nodeTemplate(node)}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
